import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Users, Search, Loader2, Plus, Eye, Pencil, Calculator,
  AlertTriangle, ChevronDown,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const TYPE_BADGE = {
  NETWORK: "bg-blue-100 text-blue-700 border-blue-200",
  REVENUE: "bg-purple-100 text-purple-700 border-purple-200",
  EXECUTION: "bg-emerald-100 text-emerald-700 border-emerald-200",
};

const PARTNER_TYPES = ["NETWORK", "REVENUE", "EXECUTION"];

function fmt(n) {
  return `\u20B9${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export default function ManagePartners() {
  const navigate = useNavigate();
  const token = localStorage.getItem("sendtoo_token");
  const headers = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };

  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState("All");
  const [statusFilter, setStatusFilter] = useState("All");
  const [search, setSearch] = useState("");
  const [openTypeFor, setOpenTypeFor] = useState(null);

  // Dialogs
  const [confirmType, setConfirmType] = useState(null);     // {partner, newType}
  const [confirmStatus, setConfirmStatus] = useState(null); // partner
  const [viewPartner, setViewPartner] = useState(null);
  const [viewLedger, setViewLedger] = useState([]);
  const [viewWallet, setViewWallet] = useState(null);

  // Create dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({
    partner_type: "NETWORK", business_name: "", owner_name: "",
    phone: "", email: "", password: "",
    hub_pincode: "", hub_address: "", hub_city: "", hub_state: "",
  });
  const [creating, setCreating] = useState(false);

  // Edit dialog
  const [editPartner, setEditPartner] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [editing, setEditing] = useState(false);

  // FM/LM preview
  const [previewPartnerId, setPreviewPartnerId] = useState("");
  const [previewPincode, setPreviewPincode] = useState("");
  const [previewResult, setPreviewResult] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const fetchPartners = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (typeFilter !== "All") params.set("partner_type", typeFilter);
      if (statusFilter === "Active") params.set("is_active", "true");
      if (statusFilter === "Inactive") params.set("is_active", "false");
      const r = await fetch(`${API}/api/v2/partners?${params.toString()}`, { headers });
      if (r.ok) setPartners(await r.json());
    } catch { /* ignore */ }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeFilter, statusFilter]);

  useEffect(() => { fetchPartners(); }, [fetchPartners]);

  const filtered = partners.filter((p) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (p.business_name || "").toLowerCase().includes(q) ||
           (p.partner_code || "").toLowerCase().includes(q) ||
           (p.email || "").toLowerCase().includes(q);
  });

  const doChangeType = async () => {
    if (!confirmType) return;
    const { partner, newType } = confirmType;
    try {
      const r = await fetch(`${API}/api/v2/partners/${partner.id}/change-type`, {
        method: "POST", headers,
        body: JSON.stringify({ partner_type: newType }),
      });
      if (r.ok) {
        toast.success("Partner type updated");
        fetchPartners();
      } else {
        const err = await r.json();
        toast.error(err.detail || "Update failed");
      }
    } catch (e) { toast.error(e.message); }
    setConfirmType(null);
    setOpenTypeFor(null);
  };

  const doToggleStatus = async () => {
    if (!confirmStatus) return;
    try {
      const r = await fetch(`${API}/api/v2/partners/${confirmStatus.id}/toggle-status`, {
        method: "POST", headers,
      });
      if (r.ok) {
        toast.success("Partner status updated");
        fetchPartners();
      } else {
        const err = await r.json();
        toast.error(err.detail || "Update failed");
      }
    } catch (e) { toast.error(e.message); }
    setConfirmStatus(null);
  };

  const openView = async (p) => {
    setViewPartner(p);
    setViewLedger([]);
    setViewWallet(null);
    try {
      const [ledgerRes, walletRes] = await Promise.all([
        fetch(`${API}/api/v2/partners/${p.id}/ledger`, { headers }),
        fetch(`${API}/api/v2/partners/${p.id}/wallet`, { headers }),
      ]);
      if (ledgerRes.ok) {
        const l = await ledgerRes.json();
        setViewLedger((Array.isArray(l) ? l : (l.items || [])).slice(0, 5));
      }
      if (walletRes.ok) setViewWallet(await walletRes.json());
    } catch { /* ignore */ }
  };

  const doCreatePartner = async () => {
    const required = ["business_name", "owner_name", "phone", "email", "password",
                      "hub_pincode", "hub_address", "hub_city", "hub_state"];
    for (const f of required) {
      if (!createForm[f]) return toast.error(`${f.replace(/_/g, " ")} is required`);
    }
    setCreating(true);
    try {
      const r = await fetch(`${API}/api/v2/partners`, {
        method: "POST", headers, body: JSON.stringify(createForm),
      });
      if (r.ok) {
        const d = await r.json();
        toast.success(`Partner created | Code: ${d.partner_code}`);
        setCreateOpen(false);
        setCreateForm({
          partner_type: "NETWORK", business_name: "", owner_name: "",
          phone: "", email: "", password: "",
          hub_pincode: "", hub_address: "", hub_city: "", hub_state: "",
        });
        fetchPartners();
      } else {
        const err = await r.json();
        toast.error(err.detail || "Create failed");
      }
    } catch (e) { toast.error(e.message); }
    setCreating(false);
  };

  const openEdit = (p) => {
    const rp = p.radius_pricing || {};
    setEditPartner(p);
    setEditForm({
      business_name: p.business_name || "",
      owner_name: p.owner_name || "",
      phone: p.phone || "",
      hub_address: p.hub_address || "",
      hub_city: p.hub_city || "",
      hub_state: p.hub_state || "",
      hub_pincode: p.hub_pincode || "",
      commission_percent: p.commission_percent ?? 10,
      payout_cycle: p.payout_cycle || "WEEKLY",
      credit_limit: p.credit_limit ?? 0,
      rp_0_5_rate: rp["0_5km"]?.rate_per_kg ?? 2,
      rp_0_5_min: rp["0_5km"]?.min_charge ?? 50,
      rp_5_10_rate: rp["5_10km"]?.rate_per_kg ?? 3,
      rp_5_10_min: rp["5_10km"]?.min_charge ?? 50,
      rp_10_15_rate: rp["10_15km"]?.rate_per_kg ?? 4,
      rp_10_15_min: rp["10_15km"]?.min_charge ?? 75,
      settlement_cycle: p.settlement_cycle || "",
      settlement_n_months: p.settlement_n_months || "",
      report_download_enabled_partner: p.report_download_enabled_partner ?? true,
      report_download_enabled_staff: p.report_download_enabled_staff ?? true,
    });
  };

  const setEditField = (k, v) => setEditForm((f) => ({ ...f, [k]: v }));

  const doEdit = async () => {
    if (!editPartner || !editForm) return;
    const rpOriginal = editPartner.radius_pricing || {};
    const payload = {};
    const directFields = [
      "business_name", "owner_name", "phone",
      "hub_address", "hub_city", "hub_state", "hub_pincode",
      "payout_cycle",
    ];
    for (const f of directFields) {
      if (editForm[f] !== (editPartner[f] || "")) payload[f] = editForm[f];
    }
    if (parseFloat(editForm.commission_percent) !== (editPartner.commission_percent ?? 10)) {
      payload.commission_percent = parseFloat(editForm.commission_percent);
    }
    if (parseFloat(editForm.credit_limit) !== (editPartner.credit_limit ?? 0)) {
      payload.credit_limit = parseFloat(editForm.credit_limit);
    }
    const newRp = {
      "0_5km":   { rate_per_kg: parseFloat(editForm.rp_0_5_rate) || 0,  min_charge: parseFloat(editForm.rp_0_5_min) || 0 },
      "5_10km":  { rate_per_kg: parseFloat(editForm.rp_5_10_rate) || 0, min_charge: parseFloat(editForm.rp_5_10_min) || 0 },
      "10_15km": { rate_per_kg: parseFloat(editForm.rp_10_15_rate) || 0, min_charge: parseFloat(editForm.rp_10_15_min) || 0 },
      "over_15km_per_km": rpOriginal.over_15km_per_km ?? 20.0,
    };
    const rpChanged =
      newRp["0_5km"].rate_per_kg !== (rpOriginal["0_5km"]?.rate_per_kg ?? 2) ||
      newRp["0_5km"].min_charge !== (rpOriginal["0_5km"]?.min_charge ?? 50) ||
      newRp["5_10km"].rate_per_kg !== (rpOriginal["5_10km"]?.rate_per_kg ?? 3) ||
      newRp["5_10km"].min_charge !== (rpOriginal["5_10km"]?.min_charge ?? 50) ||
      newRp["10_15km"].rate_per_kg !== (rpOriginal["10_15km"]?.rate_per_kg ?? 4) ||
      newRp["10_15km"].min_charge !== (rpOriginal["10_15km"]?.min_charge ?? 75);
    if (rpChanged) payload.radius_pricing = newRp;

    // Settlement & report toggles — send if changed
    const settleFields = [
      "settlement_cycle", "settlement_n_months",
      "report_download_enabled_partner", "report_download_enabled_staff",
    ];
    for (const f of settleFields) {
      const a = editForm[f];
      const b = editPartner[f];
      if (a !== b && !(a === "" && b == null)) {
        payload[f] = a === "" ? null : a;
      }
    }

    if (Object.keys(payload).length === 0) {
      toast.info("No changes to save");
      setEditPartner(null);
      return;
    }

    setEditing(true);
    try {
      const r = await fetch(`${API}/api/v2/partners/${editPartner.id}`, {
        method: "PUT", headers, body: JSON.stringify(payload),
      });
      if (r.ok) {
        toast.success("Partner updated");
        setEditPartner(null);
        setEditForm(null);
        fetchPartners();
      } else {
        const err = await r.json().catch(() => ({ detail: "Update failed" }));
        toast.error(err.detail || "Update failed");
      }
    } catch (e) { toast.error(e.message); }
    setEditing(false);
  };


  const runPreview = async () => {
    if (!previewPartnerId || previewPincode.length !== 6) {
      return toast.error("Select a partner and enter a 6-digit pincode");
    }
    const p = partners.find((x) => x.id === previewPartnerId);
    if (!p) return toast.error("Partner not found");
    setPreviewLoading(true);
    setPreviewResult(null);
    try {
      const r = await fetch(
        `${API}/api/v2/partners/distance?from_pincode=${p.hub_pincode}&to_pincode=${previewPincode}`,
        { headers }
      );
      if (r.ok) {
        setPreviewResult(await r.json());
      } else {
        toast.error("Distance calc failed");
      }
    } catch (e) { toast.error(e.message); }
    setPreviewLoading(false);
  };

  return (
    <DashboardLayout>
      <div className="space-y-5" data-testid="manage-partners">
        {/* HEADER */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Partner Management</h1>
            <p className="text-sm text-slate-500">Manage network, revenue and execution partners</p>
          </div>
          <Button onClick={() => navigate("/admin/partners/new")} data-testid="add-partner-btn">
            <Plus className="w-4 h-4 mr-1.5" /> Add Partner
          </Button>
        </div>

        {/* FILTERS */}
        <div className="bg-white border rounded-lg p-3 grid grid-cols-2 md:grid-cols-4 gap-2 items-end">
          <div>
            <label className="text-[10px] uppercase text-slate-500 font-semibold">Partner Type</label>
            <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}
                    className="w-full border border-slate-300 rounded h-9 px-2 text-sm"
                    data-testid="filter-type">
              <option>All</option>
              {PARTNER_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase text-slate-500 font-semibold">Status</label>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                    className="w-full border border-slate-300 rounded h-9 px-2 text-sm"
                    data-testid="filter-status">
              <option>All</option>
              <option>Active</option>
              <option>Inactive</option>
            </select>
          </div>
          <div className="col-span-2 md:col-span-2">
            <label className="text-[10px] uppercase text-slate-500 font-semibold">Search name or code</label>
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-2 top-2.5 text-slate-400" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)}
                     className="pl-7 h-9 text-sm" placeholder="Partner name or code..."
                     data-testid="filter-search" />
            </div>
          </div>
        </div>

        {/* TABLE */}
        <div className="bg-white border rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="text-left px-3 py-2 font-semibold">Partner Code</th>
                  <th className="text-left px-3 py-2 font-semibold">Business Name</th>
                  <th className="text-left px-3 py-2 font-semibold">Type</th>
                  <th className="text-left px-3 py-2 font-semibold">City</th>
                  <th className="text-left px-3 py-2 font-semibold">Pincode</th>
                  <th className="text-left px-3 py-2 font-semibold">Payout Cycle</th>
                  <th className="text-center px-3 py-2 font-semibold">Status</th>
                  <th className="text-right px-3 py-2 font-semibold">Commission</th>
                  <th className="text-right px-3 py-2 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={9} className="py-10 text-center">
                    <Loader2 className="w-5 h-5 animate-spin mx-auto text-slate-400" />
                  </td></tr>
                ) : filtered.length === 0 ? (
                  <tr><td colSpan={9} className="py-10 text-center text-slate-400">
                    <Users className="w-8 h-8 mx-auto mb-2 opacity-40" />
                    No partners found
                  </td></tr>
                ) : (
                  filtered.map((p) => (
                    <tr key={p.id} className="border-b border-slate-100 hover:bg-slate-50/50"
                        data-testid={`partner-row-${p.id}`}>
                      <td className="px-3 py-2 font-mono text-[11px]">{p.partner_code}</td>
                      <td className="px-3 py-2 font-medium">{p.business_name}</td>
                      <td className="px-3 py-2">
                        <Badge className={`text-[10px] border ${TYPE_BADGE[p.partner_type] || ""}`}>
                          {p.partner_type}
                        </Badge>
                      </td>
                      <td className="px-3 py-2">{p.hub_city}</td>
                      <td className="px-3 py-2 font-mono text-[11px]">{p.hub_pincode}</td>
                      <td className="px-3 py-2">
                        {p.settlement_cycle || p.payout_cycle ? (
                          <span>{p.settlement_cycle || p.payout_cycle}</span>
                        ) : (
                          <span className="italic text-slate-400">Not set</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-center">
                        <button onClick={() => setConfirmStatus(p)}
                                className="relative inline-flex items-center"
                                data-testid={`status-toggle-${p.id}`}>
                          <span className={`w-9 h-5 rounded-full p-0.5 flex items-center transition-colors ${
                            p.is_active ? "bg-emerald-500" : "bg-slate-300"
                          }`}>
                            <span className={`w-4 h-4 bg-white rounded-full shadow transition-transform ${
                              p.is_active ? "translate-x-4" : "translate-x-0"
                            }`} />
                          </span>
                        </button>
                      </td>
                      <td className="px-3 py-2 text-right">
                        {p.commission_percent != null ? `${p.commission_percent}%` : "—"}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <div className="flex gap-1 justify-end relative">
                          <Button variant="outline" size="sm" onClick={() => openView(p)}
                                  data-testid={`view-btn-${p.id}`}>
                            <Eye className="w-3.5 h-3.5" />
                          </Button>
                          <Button variant="outline" size="sm"
                                  data-testid={`edit-btn-${p.id}`}
                                  onClick={() => openEdit(p)}>
                            <Pencil className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* FM/LM DISTANCE PREVIEW */}
        <div className="bg-white border rounded-lg p-4" data-testid="fmlm-preview">
          <div className="flex items-center gap-2 mb-1">
            <Calculator className="w-4 h-4 text-indigo-600" />
            <h2 className="text-sm font-bold text-slate-800">FM/LM Distance Calculator</h2>
          </div>
          <p className="text-xs text-slate-500 mb-3">Check pickup/delivery charges from partner hub</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
            <div>
              <label className="text-[10px] uppercase text-slate-500 font-semibold">Partner</label>
              <select value={previewPartnerId} onChange={(e) => setPreviewPartnerId(e.target.value)}
                      className="w-full border border-slate-300 rounded h-9 px-2 text-sm"
                      data-testid="preview-partner">
                <option value="">Select partner...</option>
                {partners.map((p) => (
                  <option key={p.id} value={p.id}>{p.business_name} ({p.hub_pincode})</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase text-slate-500 font-semibold">To Pincode</label>
              <Input value={previewPincode} maxLength={6}
                     onChange={(e) => setPreviewPincode(e.target.value.replace(/\D/g, ""))}
                     placeholder="6-digit pincode" className="h-9 text-sm"
                     data-testid="preview-pincode" />
            </div>
            <Button onClick={runPreview} disabled={previewLoading}
                    className="bg-indigo-600 hover:bg-indigo-700" data-testid="preview-btn">
              {previewLoading ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Calculator className="w-4 h-4 mr-1.5" />}
              Calculate
            </Button>
          </div>

          {previewResult && !previewResult.error && (
            <div className="mt-4 bg-indigo-50 border border-indigo-200 rounded p-3" data-testid="preview-result">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                <div><div className="text-[10px] uppercase text-slate-500">Distance</div><div className="font-bold">{previewResult.distance_km} km</div></div>
                <div><div className="text-[10px] uppercase text-slate-500">Tier</div><div className="font-bold">{previewResult.tier}</div></div>
                <div><div className="text-[10px] uppercase text-slate-500">Rate</div><div className="font-bold">{fmt(previewResult.rate_per_kg)}/kg</div></div>
                <div><div className="text-[10px] uppercase text-slate-500">Min Charge</div><div className="font-bold">{fmt(previewResult.min_charge)}</div></div>
                <div className="col-span-2 md:col-span-4 text-xs text-slate-500 border-t pt-2">
                  Partner Hub: {previewResult.partner_hub_pincode || "—"} ({previewResult.partner_name || "no hub match"})
                </div>
              </div>
            </div>
          )}
          {previewResult?.error && (
            <div className="mt-4 bg-red-50 border border-red-200 rounded p-3 text-xs text-red-700 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" /> {previewResult.error}
            </div>
          )}
        </div>

        {/* CHANGE TYPE CONFIRM */}
        <Dialog open={!!confirmType} onOpenChange={() => setConfirmType(null)}>
          <DialogContent>
            <DialogHeader><DialogTitle>Confirm Type Change</DialogTitle></DialogHeader>
            {confirmType && (
              <p className="text-sm text-slate-600">
                Change <strong>{confirmType.partner.business_name}</strong> from{" "}
                <strong>{confirmType.partner.partner_type}</strong> to{" "}
                <strong>{confirmType.newType}</strong>?
                <br />This affects their portal access and commission structure.
              </p>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setConfirmType(null)}>Cancel</Button>
              <Button onClick={doChangeType} data-testid="confirm-change-type-btn">Confirm</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* TOGGLE STATUS CONFIRM */}
        <Dialog open={!!confirmStatus} onOpenChange={() => setConfirmStatus(null)}>
          <DialogContent>
            <DialogHeader><DialogTitle>Confirm Status Change</DialogTitle></DialogHeader>
            {confirmStatus && (
              <p className="text-sm text-slate-600">
                Are you sure you want to {confirmStatus.is_active ? "disable" : "enable"}{" "}
                <strong>{confirmStatus.business_name}</strong>?
              </p>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setConfirmStatus(null)}>Cancel</Button>
              <Button onClick={doToggleStatus} data-testid="confirm-toggle-status-btn">Confirm</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* VIEW DETAILS */}
        <Dialog open={!!viewPartner} onOpenChange={() => setViewPartner(null)}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{viewPartner?.business_name}</DialogTitle></DialogHeader>
            {viewPartner && (
              <div className="space-y-4 text-sm">
                <div className="grid grid-cols-2 gap-3">
                  <Detail label="Partner Code" value={viewPartner.partner_code} mono />
                  <Detail label="Type" value={viewPartner.partner_type} />
                  <Detail label="Owner" value={viewPartner.owner_name} />
                  <Detail label="Email" value={viewPartner.email} />
                  <Detail label="Phone" value={viewPartner.phone} />
                  <Detail label="Hub City" value={viewPartner.hub_city} />
                  <Detail label="Hub Pincode" value={viewPartner.hub_pincode} mono />
                  <Detail label="Hub State" value={viewPartner.hub_state} />
                  <Detail label="Payout Cycle" value={viewPartner.payout_cycle || "WEEKLY"} />
                  <Detail label="Commission %" value={`${viewPartner.commission_percent || 10}%`} />
                </div>
                <div className="border-t pt-3">
                  <h4 className="text-xs font-bold text-slate-700 uppercase mb-2">Wallet</h4>
                  {viewWallet ? (
                    <div className="grid grid-cols-2 gap-3">
                      <Detail label="Balance" value={fmt(viewWallet.balance)} />
                      <Detail label="Credit Limit" value={fmt(viewWallet.credit_limit)} />
                    </div>
                  ) : <div className="text-xs text-slate-400">Loading wallet...</div>}
                </div>
                <div className="border-t pt-3">
                  <h4 className="text-xs font-bold text-slate-700 uppercase mb-2">Radius Pricing</h4>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead><tr className="text-slate-500">
                        <th className="text-left py-1">Tier</th>
                        <th className="text-right py-1">Rate/kg</th>
                        <th className="text-right py-1">Min Charge</th>
                      </tr></thead>
                      <tbody>
                        {["0_5km", "5_10km", "10_15km"].map((k) => {
                          const slab = viewPartner.radius_pricing?.[k] || {};
                          return (
                            <tr key={k} className="border-t border-slate-100">
                              <td className="py-1">{k.replace("_", "-").replace("km", " km")}</td>
                              <td className="text-right py-1">{fmt(slab.rate_per_kg)}</td>
                              <td className="text-right py-1">{fmt(slab.min_charge)}</td>
                            </tr>
                          );
                        })}
                        <tr className="border-t border-slate-100">
                          <td className="py-1">Over 15 km</td>
                          <td className="text-right py-1" colSpan={2}>
                            {fmt(viewPartner.radius_pricing?.over_15km_per_km || 20)}/km
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
                <div className="border-t pt-3">
                  <h4 className="text-xs font-bold text-slate-700 uppercase mb-2">Recent Ledger (5)</h4>
                  {viewLedger.length === 0 ? (
                    <div className="text-xs text-slate-400">No entries</div>
                  ) : (
                    <div className="space-y-1.5">
                      {viewLedger.map((e, i) => (
                        <div key={i} className="flex justify-between text-xs border rounded p-2">
                          <span>{e.action || e.txn_type || "—"}</span>
                          <span className="font-medium text-emerald-600">{fmt(e.commission_amount || e.amount || 0)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* CREATE PARTNER */}
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Add Partner</DialogTitle></DialogHeader>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              <Field label="Partner Type">
                <select value={createForm.partner_type}
                        onChange={(e) => setCreateForm({ ...createForm, partner_type: e.target.value })}
                        className="w-full border border-slate-300 rounded h-9 px-2 text-sm"
                        data-testid="create-type">
                  {PARTNER_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </Field>
              <Field label="Business Name">
                <Input value={createForm.business_name}
                       onChange={(e) => setCreateForm({ ...createForm, business_name: e.target.value })}
                       data-testid="create-business-name" />
              </Field>
              <Field label="Owner Name">
                <Input value={createForm.owner_name}
                       onChange={(e) => setCreateForm({ ...createForm, owner_name: e.target.value })}
                       data-testid="create-owner-name" />
              </Field>
              <Field label="Phone">
                <Input value={createForm.phone}
                       onChange={(e) => setCreateForm({ ...createForm, phone: e.target.value })}
                       data-testid="create-phone" />
              </Field>
              <Field label="Email">
                <Input type="email" value={createForm.email}
                       onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                       data-testid="create-email" />
              </Field>
              <Field label="Password">
                <Input type="password" value={createForm.password}
                       onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
                       data-testid="create-password" />
              </Field>
              <Field label="Hub Pincode">
                <Input value={createForm.hub_pincode} maxLength={6}
                       onChange={(e) => setCreateForm({ ...createForm, hub_pincode: e.target.value.replace(/\D/g, "") })}
                       data-testid="create-hub-pincode" />
              </Field>
              <Field label="Hub City">
                <Input value={createForm.hub_city}
                       onChange={(e) => setCreateForm({ ...createForm, hub_city: e.target.value })}
                       data-testid="create-hub-city" />
              </Field>
              <Field label="Hub State">
                <Input value={createForm.hub_state}
                       onChange={(e) => setCreateForm({ ...createForm, hub_state: e.target.value })}
                       data-testid="create-hub-state" />
              </Field>
              <Field label="Hub Address">
                <Input value={createForm.hub_address}
                       onChange={(e) => setCreateForm({ ...createForm, hub_address: e.target.value })}
                       data-testid="create-hub-address" />
              </Field>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
              <Button onClick={doCreatePartner} disabled={creating} data-testid="submit-create-partner">
                {creating ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Plus className="w-4 h-4 mr-1.5" />}
                Create Partner
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* EDIT PARTNER */}
        <Dialog open={!!editPartner} onOpenChange={(o) => { if (!o) { setEditPartner(null); setEditForm(null); } }}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Edit Partner — {editPartner?.business_name}</DialogTitle>
            </DialogHeader>
            {editForm && (
              <div className="space-y-4 text-sm" data-testid="edit-partner-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Field label="Business Name">
                    <Input value={editForm.business_name}
                           onChange={(e) => setEditField("business_name", e.target.value)}
                           data-testid="edit-business-name" />
                  </Field>
                  <Field label="Owner Name">
                    <Input value={editForm.owner_name}
                           onChange={(e) => setEditField("owner_name", e.target.value)}
                           data-testid="edit-owner-name" />
                  </Field>
                  <Field label="Phone">
                    <Input value={editForm.phone}
                           onChange={(e) => setEditField("phone", e.target.value)}
                           data-testid="edit-phone" />
                  </Field>
                  <Field label="Hub Pincode">
                    <Input value={editForm.hub_pincode} maxLength={6}
                           onChange={(e) => setEditField("hub_pincode", e.target.value.replace(/\D/g, ""))}
                           data-testid="edit-hub-pincode" />
                  </Field>
                  <Field label="Hub City">
                    <Input value={editForm.hub_city}
                           onChange={(e) => setEditField("hub_city", e.target.value)}
                           data-testid="edit-hub-city" />
                  </Field>
                  <Field label="Hub State">
                    <Input value={editForm.hub_state}
                           onChange={(e) => setEditField("hub_state", e.target.value)}
                           data-testid="edit-hub-state" />
                  </Field>
                  <div className="md:col-span-2">
                    <Field label="Hub Address">
                      <Input value={editForm.hub_address}
                             onChange={(e) => setEditField("hub_address", e.target.value)}
                             data-testid="edit-hub-address" />
                    </Field>
                  </div>
                  <Field label="Commission %">
                    <Input type="number" value={editForm.commission_percent}
                           onChange={(e) => setEditField("commission_percent", e.target.value)}
                           data-testid="edit-commission" />
                  </Field>
                  <Field label="Payout Cycle">
                    <select value={editForm.payout_cycle}
                            onChange={(e) => setEditField("payout_cycle", e.target.value)}
                            className="w-full border border-slate-300 rounded h-9 px-2 text-sm"
                            data-testid="edit-payout-cycle">
                      <option value="DAILY">DAILY</option>
                      <option value="WEEKLY">WEEKLY</option>
                      <option value="MONTHLY">MONTHLY</option>
                    </select>
                  </Field>
                  <Field label="Credit Limit (\u20B9)">
                    <Input type="number" value={editForm.credit_limit}
                           onChange={(e) => setEditField("credit_limit", e.target.value)}
                           data-testid="edit-credit-limit" />
                  </Field>
                </div>

                {/* RADIUS PRICING */}
                <div className="border-t pt-3">
                  <h4 className="text-xs font-bold text-slate-700 uppercase mb-3">Radius Pricing</h4>
                  <div className="space-y-2">
                    {[
                      { key: "0_5", label: "0-5 km" },
                      { key: "5_10", label: "5-10 km" },
                      { key: "10_15", label: "10-15 km" },
                    ].map(({ key, label }) => (
                      <div key={key} className="grid grid-cols-3 gap-3 items-center">
                        <div className="text-xs font-semibold text-slate-600">{label}</div>
                        <div>
                          <label className="block text-[10px] uppercase text-slate-500 mb-1">Rate per kg (\u20B9)</label>
                          <Input type="number" value={editForm[`rp_${key}_rate`]}
                                 onChange={(e) => setEditField(`rp_${key}_rate`, e.target.value)}
                                 data-testid={`edit-rp-${key}-rate`} />
                        </div>
                        <div>
                          <label className="block text-[10px] uppercase text-slate-500 mb-1">Min charge (\u20B9)</label>
                          <Input type="number" value={editForm[`rp_${key}_min`]}
                                 onChange={(e) => setEditField(`rp_${key}_min`, e.target.value)}
                                 data-testid={`edit-rp-${key}-min`} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* SETTLEMENT + REPORTS */}
                <div className="border-t pt-3">
                  <h4 className="text-xs font-bold text-slate-700 uppercase mb-3">Settlement & Reports</h4>
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Settlement Cycle">
                      <select value={editForm.settlement_cycle || ""}
                              onChange={(e) => setEditField("settlement_cycle", e.target.value)}
                              data-testid="edit-settlement-cycle"
                              className="w-full border border-slate-200 rounded px-2 py-1.5 text-sm">
                        <option value="">— select —</option>
                        <option value="DAILY">DAILY</option>
                        <option value="WEEKLY">WEEKLY</option>
                        <option value="FORTNIGHTLY">FORTNIGHTLY</option>
                        <option value="MONTHLY">MONTHLY</option>
                        <option value="MULTI_MONTH">MULTI_MONTH</option>
                      </select>
                    </Field>
                    {editForm.settlement_cycle === "MULTI_MONTH" && (
                      <Field label="Every N months">
                        <Input type="number" min="1"
                               value={editForm.settlement_n_months || ""}
                               onChange={(e) => setEditField("settlement_n_months", e.target.value)}
                               data-testid="edit-settlement-n-months" />
                      </Field>
                    )}
                  </div>
                  <div className="mt-3 space-y-2">
                    <label className="flex items-center gap-2 text-xs text-slate-700">
                      <input type="checkbox"
                             checked={!!editForm.report_download_enabled_partner}
                             onChange={(e) => setEditField("report_download_enabled_partner", e.target.checked)}
                             data-testid="edit-report-partner-toggle" />
                      Partner can download commission report
                    </label>
                    <label className="flex items-center gap-2 text-xs text-slate-700">
                      <input type="checkbox"
                             checked={!!editForm.report_download_enabled_staff}
                             onChange={(e) => setEditField("report_download_enabled_staff", e.target.checked)}
                             data-testid="edit-report-staff-toggle" />
                      Staff can view this partner&apos;s report
                    </label>
                  </div>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => { setEditPartner(null); setEditForm(null); }}>
                Cancel
              </Button>
              <Button onClick={doEdit} disabled={editing} data-testid="submit-edit-partner">
                {editing ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Pencil className="w-4 h-4 mr-1.5" />}
                Save Changes
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

      </div>
    </DashboardLayout>
  );
}

function Detail({ label, value, mono }) {
  return (
    <div>
      <div className="text-[10px] uppercase text-slate-500 font-semibold">{label}</div>
      <div className={`text-sm text-slate-800 ${mono ? "font-mono" : ""}`}>{value || "—"}</div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-[10px] uppercase text-slate-500 font-semibold mb-1">{label}</label>
      {children}
    </div>
  );
}
